<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { centsToWholeUnits, formatWholeNumber } from '@/utils/poolaReportFormat.js';

const props = defineProps({
  // { poolcreditReturnedCents, distinctEarnerCount, members, completed, total } — see
  // useSupplierDealReport's composeHero.
  hero: { type: Object, required: true },
});

const { t } = useI18n({ useScope: 'global' });

const poolcreditWhole = computed(() => centsToWholeUnits(props.hero.poolcreditReturnedCents));
</script>

<template>
  <div class="report-hero" data-report-section="hero">
    <div class="report-hero__main">
      <span class="report-hero__trophy" aria-hidden="true">🎁</span>
      <p class="report-hero__label">{{ t('poola.report.hero.label') }}</p>
      <p class="report-hero__value">
        <span class="num">{{ t('poola.report.hero.value', { n: poolcreditWhole }) }}</span>
      </p>
      <p class="report-hero__helper">
        {{
          t('poola.report.hero.helper', { earners: formatWholeNumber(hero.distinctEarnerCount) })
        }}
      </p>
    </div>
    <div class="report-hero__tiles">
      <div class="report-hero__tile report-hero__tile--gold">
        <p class="report-hero__tile-label">{{ t('poola.report.tiles.completed.label') }}</p>
        <p class="report-hero__tile-value">
          <span class="num">{{
            t('poola.report.tiles.completed.value', {
              c: formatWholeNumber(hero.completed),
              t: formatWholeNumber(hero.total),
            })
          }}</span>
        </p>
        <p class="report-hero__tile-helper">{{ t('poola.report.tiles.completed.helper') }}</p>
      </div>
      <div class="report-hero__tile report-hero__tile--violet">
        <p class="report-hero__tile-label">{{ t('poola.report.tiles.members.label') }}</p>
        <p class="report-hero__tile-value">
          <span class="num">{{
            t('poola.report.tiles.members.value', { n: formatWholeNumber(hero.members) })
          }}</span>
        </p>
        <p class="report-hero__tile-helper">{{ t('poola.report.tiles.members.helper') }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.report-hero {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-inline: var(--poolit-spacing-md, 16px);
}

.num {
  font-variant-numeric: tabular-nums;
}

.report-hero__main {
  position: relative;
  background: var(--report-teal-bg);
  border: 1px solid var(--report-teal-line);
  border-radius: var(--poolit-radius-lg, 16px);
  padding: 18px 18px 16px;
  overflow: hidden;
}

.report-hero__trophy {
  position: absolute;
  inset-inline-start: 16px;
  top: 14px;
  font-size: 22px;
  opacity: 0.9;
}

.report-hero__label {
  margin: 0;
  color: var(--report-teal-deep);
  font-size: 13px;
  font-weight: var(--poolit-font-weight-medium, 500);
  opacity: 0.92;
}

.report-hero__value {
  margin: 6px 0 4px;
  color: var(--report-teal-deep);
  font-size: 2.25rem;
  font-weight: var(--poolit-font-weight-bold, 700);
  line-height: 1.05;
  letter-spacing: -0.02em;
}

.report-hero__helper {
  margin: 0;
  color: var(--report-teal-deep);
  opacity: 0.82;
  font-size: 12.5px;
  line-height: 1.5;
}

.report-hero__tiles {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.report-hero__tile {
  border-radius: var(--poolit-radius-lg, 16px);
  padding: 14px;
  border: 1px solid var(--report-hairline);
  background: var(--report-surface);
}

.report-hero__tile--gold {
  background: var(--report-gold-bg);
  border-color: var(--report-gold-line);
}

.report-hero__tile--violet {
  background: var(--report-violet-bg);
  border-color: var(--report-violet-line);
}

.report-hero__tile-label {
  margin: 0;
  font-size: 12px;
  font-weight: var(--poolit-font-weight-medium, 500);
  line-height: 1.35;
  color: var(--report-text-soft);
}

.report-hero__tile--gold .report-hero__tile-label,
.report-hero__tile--gold .report-hero__tile-value {
  color: var(--report-gold-deep);
}

.report-hero__tile--violet .report-hero__tile-label,
.report-hero__tile--violet .report-hero__tile-value {
  color: var(--report-violet-deep);
}

.report-hero__tile-value {
  margin: 6px 0 3px;
  font-size: 1.625rem;
  font-weight: var(--poolit-font-weight-bold, 700);
  letter-spacing: -0.01em;
}

.report-hero__tile-helper {
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  opacity: 0.85;
}

.report-hero__tile--gold .report-hero__tile-helper {
  color: var(--report-gold-deep);
}

.report-hero__tile--violet .report-hero__tile-helper {
  color: var(--report-violet-deep);
}
</style>
